// Nota de crédito directa (pedido de Rodrigo 2026-10-23, como la NCV de Random): se busca la factura o
// boleta (por folio, o las de un cliente de los últimos 90 días), se elige el motivo y, si la
// mercadería vuelve, a qué bodega entra (la de salida por omisión; lo mermado, a la de mermas). Solo
// con el permiso `ventas.nc.directa`: el permiso es la autorización (no pide firma) y queda auditado.
// Lo que viene de la ruta (guía de devolución autorizada al recibir el camión) es otro flujo.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { api, pedirArchivo } from '../api/cliente';
import { useUbicaciones } from '../api/consultas';
import type { ClienteEncontrado } from '../api/contratos';
import { useIntentos } from '../api/idempotencia';
import { PanelError } from '../comun/Estados';
import { Grilla, type Columna } from '../comun/Grilla';
import { fecha, pesos } from '../comun/formato';
import { useSesion } from '../sesion/SesionContexto';
import { BuscadorCliente } from './BuscadorCliente';

type DocumentoNc = {
  documentoId: string;
  tipoDte: number;
  folio: number | null;
  fechaEmision: string | null;
  total: string;
  saldo: string;
  enviado: boolean;
  clienteRazonSocial: string;
  clienteRut: string | null;
};
type LineaNc = {
  numero: number;
  sku: string;
  descripcion: string;
  unidad: string;
  cantidad: string;
  bruto: string;
  acreditado: string;
  disponible: string;
  enCliente: string;
};
type ContextoNc = {
  documentoId: string;
  tipoDte: number;
  folio: number | null;
  fechaEmision: string | null;
  total: string;
  enviado: boolean;
  anulado: boolean;
  clienteRazonSocial: string;
  clienteRut: string | null;
  ubicacionOrigenId: string;
  lineas: LineaNc[];
  /** Rechazo en la ruta que espera esta NC: lo que volvió en el camión (en la unidad de la línea). */
  rechazo: {
    odNumero: number;
    fecha: string;
    lineas: Array<{ lineaNumero: number; cantidad: string }>;
  } | null;
};
type Resultado = {
  notas: Array<{
    ajusteId: string;
    numero: string;
    clase: string;
    bruto: string;
    dteDocumentoId: string | null;
  }>;
  devoluciones: Array<{ devolucionId: string; numero: string }>;
  /** Lo que se resolvió del rechazo en la ruta (la parada ya no pide NC; la nota, si se cerró). */
  rechazo: { odd: number[]; notasCerradas: string[]; errores: string[] } | null;
};
/** Una parada que el cliente no quiso y espera su NC (2026-10-31). */
type RechazoPorNc = {
  odId: string;
  odNumero: number;
  fecha: string;
  notaId: string;
  numeroVisible: string;
  cliente: string;
  documentoId: string | null;
  tipoDte: number | null;
  folio: number | null;
  lineas: Array<{ lineaNumero: number; sku: string; descripcion: string; rechazadoBase: string }>;
};

type Motivo = 'ANULACION' | 'DEVOLUCION' | 'MONTO' | 'TEXTO';
const MOTIVOS: Array<{ id: Motivo; titulo: string; detalle: string }> = [
  {
    id: 'ANULACION',
    titulo: 'Anular el documento',
    detalle: 'Anulación completa (todas las líneas).',
  },
  {
    id: 'DEVOLUCION',
    titulo: 'Devolución de mercadería',
    detalle: 'Total o parcial, por línea y cantidad.',
  },
  {
    id: 'MONTO',
    titulo: 'Corrige monto',
    detalle: 'Descuento, bonificación o precio mal cobrado, por línea.',
  },
  { id: 'TEXTO', titulo: 'Corrige texto', detalle: 'Razón social, giro u otro dato (sin montos).' },
];
const CAUSAS_DEVOLUCION = [
  ['MAL_FACTURADO', 'Producto mal facturado'],
  ['RECHAZADO', 'Producto rechazado'],
  ['MERMADO', 'Producto mermado o en mal estado'],
  ['FALTANTE', 'Producto faltante'],
  ['OTRO', 'Otro motivo'],
] as const;
const CAUSAS_MONTO = [
  ['DESCUENTO', 'Descuento'],
  ['BONIFICACION', 'Bonificación'],
  ['PRECIO', 'Precio mal cobrado'],
] as const;
const RECIBEN = ['BODEGA', 'MESON', 'MERMA', 'CUARENTENA'];

const nombreDoc = (t: number) => (t === 39 ? 'Boleta' : t === 33 ? 'Factura' : `DTE ${t}`);
const num = (s: string) => Number(s || 0);
const cant = (s: string) => {
  const n = Number(s);
  return Number.isInteger(n) ? String(n) : n.toLocaleString('es-CL', { maximumFractionDigits: 4 });
};

export function NotaCreditoDirecta({
  documentoInicial,
}: {
  documentoInicial?: string | undefined;
}) {
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const [tipoDte, setTipoDte] = useState('');
  const [folio, setFolio] = useState('');
  const [cliente, setCliente] = useState<ClienteEncontrado | null>(null);
  const [buscandoCliente, setBuscandoCliente] = useState(false);
  const [filtro, setFiltro] = useState<{
    folio?: string;
    tipoDte?: string;
    clienteEntidadId?: string;
  }>({});
  // Desde «Hacer NC» de la rendición de ODD llega abierta en la factura del rechazo.
  const [documentoId, setDocumentoId] = useState<string | null>(documentoInicial ?? null);

  const docs = useQuery({
    queryKey: ['emp', empresaId, 'nc-directa', 'documentos', filtro],
    queryFn: () =>
      api.get<{ items: DocumentoNc[] }>('/ventas/nc-directa/documentos', {
        query: { empresaId, ...filtro },
      }),
    enabled: puede('ventas.nc.directa') && (!!filtro.folio || !!filtro.clienteEntidadId),
  });
  const rechazos = useQuery({
    queryKey: ['emp', empresaId, 'nc-directa', 'rechazos'],
    queryFn: () =>
      api.get<{ items: RechazoPorNc[] }>('/ventas/nc-directa/rechazos', { query: { empresaId } }),
    enabled: puede('ventas.nc.directa'),
  });
  const porHacer = (rechazos.data?.items ?? []).filter((r) => r.documentoId);

  if (!puede('ventas.nc.directa'))
    return (
      <section className="pantalla" aria-label="Nota de crédito directa">
        <h2>Nota de crédito directa</h2>
        <p className="apagado">
          La nota de crédito directa la hace solo quien tiene el permiso «NC directa»
          (ventas.nc.directa).
        </p>
      </section>
    );

  const buscar = () => {
    setDocumentoId(null);
    setFiltro({
      ...(folio ? { folio } : {}),
      ...(tipoDte ? { tipoDte } : {}),
      ...(cliente ? { clienteEntidadId: cliente.id } : {}),
    });
  };
  const columnas: Columna<DocumentoNc>[] = [
    { id: 't', titulo: 'TD', celda: (d) => nombreDoc(d.tipoDte), ancho: '6rem' },
    {
      id: 'f',
      titulo: 'Folio',
      celda: (d) => <span className="mono">{d.folio ?? '—'}</span>,
      ancho: '6rem',
    },
    {
      id: 'e',
      titulo: 'Emisión',
      celda: (d) => (d.fechaEmision ? fecha(d.fechaEmision) : '—'),
      ancho: '7rem',
    },
    { id: 'c', titulo: 'Cliente', celda: (d) => d.clienteRazonSocial },
    { id: 'm', titulo: 'Monto', celda: (d) => pesos(d.total), alinear: 'der', ancho: '8rem' },
    { id: 's', titulo: 'Saldo', celda: (d) => pesos(d.saldo), alinear: 'der', ancho: '8rem' },
    {
      id: 'x',
      titulo: 'Estado',
      celda: (d) =>
        num(d.saldo) <= 0
          ? 'acreditado entero'
          : d.enviado
            ? 'enviado al SII'
            : 'sin enviar al SII',
      ancho: '9rem',
    },
  ];

  return (
    <section className="pantalla nc-directa" aria-label="Nota de crédito directa">
      <h2>Nota de crédito directa</h2>
      {!documentoId && porHacer.length > 0 && (
        <fieldset className="recuadro-nc ncd-rechazos">
          <legend>Rechazos en la ruta que esperan su NC ({porHacer.length})</legend>
          <p className="ayuda">
            El cliente no quiso lo devuelto: volvió en el camión y bodega ya lo recibió. Mientras
            falte la NC, la ODD no se cierra y la nota no vuelve a salir.
          </p>
          <table className="tabla-fija" aria-label="Rechazos que esperan NC">
            <thead>
              <tr>
                <th>ODD</th>
                <th>Fecha</th>
                <th>Nota</th>
                <th>Cliente</th>
                <th>Documento</th>
                <th>Lo que volvió</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {porHacer.map((r) => (
                <tr key={`${r.odId}:${r.notaId}`}>
                  <td className="mono">{r.odNumero}</td>
                  <td>{fecha(r.fecha)}</td>
                  <td className="mono">{r.numeroVisible}</td>
                  <td>{r.cliente}</td>
                  <td className="mono">
                    {r.tipoDte ? nombreDoc(r.tipoDte) : '—'} {r.folio ?? ''}
                  </td>
                  <td>
                    {r.lineas.map((l) => `${cant(l.rechazadoBase)} ${l.descripcion}`).join(', ')}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="primario"
                      onClick={() => setDocumentoId(r.documentoId)}
                    >
                      Hacer NC
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </fieldset>
      )}
      <fieldset className="recuadro-nc">
        <legend>Documento a corregir</legend>
        <div className="ncd-busqueda">
          <label className="campo">
            <span className="etiqueta">Tipo</span>
            <select
              aria-label="Tipo de documento"
              value={tipoDte}
              onChange={(e) => setTipoDte(e.target.value)}
            >
              <option value="">Factura o boleta</option>
              <option value="33">Factura</option>
              <option value="39">Boleta</option>
            </select>
          </label>
          <label className="campo">
            <span className="etiqueta">Folio</span>
            <input
              aria-label="Folio"
              inputMode="numeric"
              value={folio}
              data-autofoco
              onChange={(e) => setFolio(e.target.value.replace(/\D/g, ''))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  buscar();
                }
              }}
            />
          </label>
          <div className="campo">
            <span className="etiqueta">Cliente (F2)</span>
            <button
              type="button"
              className="ncd-cliente"
              onClick={() => setBuscandoCliente(true)}
              onKeyDown={(e) => {
                if (e.key === 'F2') {
                  e.preventDefault();
                  setBuscandoCliente(true);
                }
              }}
            >
              {cliente ? cliente.razon_social : 'Elegir cliente…'}
            </button>
          </div>
          {cliente && (
            <button type="button" onClick={() => setCliente(null)} aria-label="Quitar cliente">
              ✕
            </button>
          )}
          <button type="button" className="primario" onClick={buscar}>
            Buscar (Enter)
          </button>
        </div>
        <p className="ayuda">
          Por folio, o los documentos del cliente de los últimos 90 días (como en Random).
        </p>
        {docs.isError && <PanelError error={docs.error} />}
        {(filtro.folio || filtro.clienteEntidadId) && !documentoId && (
          <Grilla
            etiqueta="Documentos"
            columnas={columnas}
            filas={docs.data?.items ?? []}
            clave={(d) => d.documentoId}
            cargando={docs.isFetching}
            autofoco
            vacio="Sin facturas ni boletas con ese criterio"
            alAbrir={(d) => setDocumentoId(d.documentoId)}
          />
        )}
      </fieldset>
      {buscandoCliente && (
        <BuscadorCliente
          empresaId={empresaId}
          alElegir={(c) => {
            setCliente(c);
            setBuscandoCliente(false);
            setFiltro({ ...(tipoDte ? { tipoDte } : {}), clienteEntidadId: c.id });
            setDocumentoId(null);
          }}
          alCerrar={() => setBuscandoCliente(false)}
        />
      )}
      {documentoId && (
        <FormularioNc
          key={documentoId}
          empresaId={empresaId}
          documentoId={documentoId}
          alCambiarDocumento={() => setDocumentoId(null)}
        />
      )}
    </section>
  );
}

function FormularioNc({
  empresaId,
  documentoId,
  alCambiarDocumento,
}: {
  empresaId: string;
  documentoId: string;
  alCambiarDocumento: () => void;
}) {
  const qc = useQueryClient();
  const intentos = useIntentos();
  const ctx = useQuery({
    queryKey: ['emp', empresaId, 'nc-directa', 'contexto', documentoId],
    queryFn: () => api.get<ContextoNc>(`/ventas/nc-directa/documentos/${documentoId}`),
  });
  const ubicaciones = useUbicaciones(empresaId);
  const reciben = useMemo(
    () => (ubicaciones.data ?? []).filter((u) => u.tipo && RECIBEN.includes(u.tipo)),
    [ubicaciones.data],
  );
  const merma = reciben.find((u) => u.tipo === 'MERMA');
  const [motivo, setMotivo] = useState<Motivo>('DEVOLUCION');
  const [causaDev, setCausaDev] = useState<string>('MAL_FACTURADO');
  const [causaMonto, setCausaMonto] = useState<string>('DESCUENTO');
  const [mueve, setMueve] = useState(true);
  const [ubicacionId, setUbicacionId] = useState('');
  const [ubicacionTocada, setUbicacionTocada] = useState(false);
  const [valores, setValores] = useState<Record<number, string>>({});
  const [texto, setTexto] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [resultado, setResultado] = useState<Resultado | null>(null);
  const [errorPdf, setErrorPdf] = useState<unknown>(null);
  const [prellenada, setPrellenada] = useState(false);

  // La factura de un rechazo en la ruta llega lista: devolución por producto rechazado, con lo que
  // volvió en el camión (bodega ya lo recibió). Se revisa y se emite.
  useEffect(() => {
    const r = ctx.data?.rechazo;
    if (prellenada || !ctx.data) return;
    setPrellenada(true);
    if (!r) return;
    setMotivo('DEVOLUCION');
    setCausaDev('RECHAZADO');
    setValores(Object.fromEntries(r.lineas.map((l) => [l.lineaNumero, cant(l.cantidad)])));
    setDescripcion(`Rechazado en la entrega (ODD N° ${r.odNumero}), volvió en el camión`);
  }, [ctx.data, prellenada]);

  // La bodega por omisión: la de salida; lo mermado, a la bodega de mermas (si no la eligió a mano).
  useEffect(() => {
    if (ubicacionTocada || !ctx.data) return;
    setUbicacionId(
      motivo === 'DEVOLUCION' && causaDev === 'MERMADO' && merma
        ? merma.id
        : ctx.data.ubicacionOrigenId,
    );
  }, [ctx.data, motivo, causaDev, merma, ubicacionTocada]);

  const emitir = useMutation({
    mutationFn: (cuerpo: Record<string, unknown>) =>
      api.post<Resultado>('/ventas/nc-directa', cuerpo, {
        idempotencia: intentos.clave('nc-directa', cuerpo),
      }),
    onSuccess: async (r) => {
      intentos.cerrar();
      setResultado(r);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['emp', empresaId, 'nc-directa'] }),
        qc.invalidateQueries({ queryKey: ['emp', empresaId, 'correcciones-venta'] }),
        qc.invalidateQueries({ queryKey: ['emp', empresaId, 'disponibilidad'] }),
        qc.invalidateQueries({ queryKey: ['emp', empresaId, 'tesoreria', 'rutas'] }),
      ]);
    },
    onError: (e) => intentos.cerrar(e),
  });

  if (ctx.isError) return <PanelError error={ctx.error} />;
  if (!ctx.data) return <p className="apagado">Cargando el documento…</p>;
  const c = ctx.data;
  const hayEnCliente = c.lineas.some((l) => num(l.enCliente) > 0);
  // Lo que volvió en el camión ya está en bodega: si la NC es solo por eso, no mueve stock.
  const volvio = new Map((c.rechazo?.lineas ?? []).map((l) => [l.lineaNumero, num(l.cantidad)]));
  const soloLoQueVolvio =
    motivo === 'DEVOLUCION' &&
    volvio.size > 0 &&
    c.lineas.some((l) => num(valores[l.numero] ?? '') > 0) &&
    c.lineas.every((l) => num(valores[l.numero] ?? '') <= (volvio.get(l.numero) ?? 0));
  const preguntaStock =
    (motivo === 'DEVOLUCION' || motivo === 'ANULACION') && hayEnCliente && !soloLoQueVolvio;
  const pideCantidad = motivo === 'DEVOLUCION';
  const pideMonto = motivo === 'MONTO';

  // Monto estimado de la NC (el definitivo lo calcula el ERP con el bruto proporcional).
  const estimado = c.lineas.reduce((a, l) => {
    if (motivo === 'ANULACION') return a + num(l.disponible);
    if (motivo === 'MONTO') return a + Math.min(num(valores[l.numero] ?? ''), num(l.disponible));
    if (motivo === 'DEVOLUCION') {
      const u = num(valores[l.numero] ?? '');
      return (
        a + Math.min(Math.round((num(l.bruto) * u) / (num(l.cantidad) || 1)), num(l.disponible))
      );
    }
    return a;
  }, 0);

  const problemas: string[] = [];
  if (!c.enviado)
    problemas.push(
      'El documento aún no está enviado al SII: antes del envío se anula, no lleva NC.',
    );
  if (c.anulado) problemas.push('El documento ya está anulado con una NC.');
  if (descripcion.trim().length < 3) problemas.push('Escribe qué pasó (descripción).');
  if (motivo === 'TEXTO' && texto.trim().length < 3)
    problemas.push('Escribe el texto que corrige.');
  if ((pideCantidad || pideMonto) && !c.lineas.some((l) => num(valores[l.numero] ?? '') > 0))
    problemas.push(
      pideCantidad
        ? 'Indica cuánto se devuelve en alguna línea.'
        : 'Indica el monto en alguna línea.',
    );
  if (pideCantidad && mueve && preguntaStock)
    for (const l of c.lineas)
      if (num(valores[l.numero] ?? '') > num(l.enCliente))
        problemas.push(`Línea ${l.numero}: vuelve más de lo que salió (${cant(l.enCliente)}).`);
  if (pideMonto)
    for (const l of c.lineas)
      if (num(valores[l.numero] ?? '') > num(l.disponible))
        problemas.push(
          `Línea ${l.numero}: el monto supera lo disponible (${pesos(l.disponible)}).`,
        );
  if (preguntaStock && mueve && !ubicacionId) problemas.push('Elige la bodega a la que entra.');

  const cuerpo = () => ({
    documentoOriginalId: documentoId,
    motivo,
    descripcion: descripcion.trim(),
    ...(motivo === 'DEVOLUCION' ? { causaDevolucion: causaDev } : {}),
    ...(motivo === 'MONTO' ? { causaMonto } : {}),
    ...(motivo === 'TEXTO' ? { textoCorreccion: texto.trim() } : {}),
    ...(pideCantidad || pideMonto
      ? {
          lineas: c.lineas
            .filter((l) => num(valores[l.numero] ?? '') > 0)
            .map((l) =>
              pideCantidad
                ? { lineaNumero: l.numero, cantidad: String(num(valores[l.numero]!)) }
                : { lineaNumero: l.numero, bruto: String(Math.round(num(valores[l.numero]!))) },
            ),
        }
      : {}),
    mueveStock: preguntaStock && mueve,
    ...(preguntaStock && mueve ? { ubicacionId } : {}),
  });
  const enviar = () => {
    if (problemas.length || emitir.isPending || resultado) return;
    emitir.mutate(cuerpo());
  };
  const imprimir = async (dteId: string) => {
    setErrorPdf(null);
    try {
      const pdf = await pedirArchivo('GET', `/dte/documentos/${dteId}/pdf`, {
        query: { ejemplar: 'ambos' },
      });
      window.open(URL.createObjectURL(pdf), '_blank', 'noopener');
    } catch (e) {
      setErrorPdf(e);
    }
  };

  return (
    <div
      className="ncd-formulario"
      onKeyDown={(e) => {
        if (e.key === 'F10') {
          e.preventDefault();
          enviar();
        }
      }}
    >
      <fieldset className="recuadro-nc">
        <legend>
          {nombreDoc(c.tipoDte)} N° {c.folio ?? '—'}
        </legend>
        <div className="ncd-cabecera">
          <span>
            <small>Cliente</small>
            <b>{c.clienteRazonSocial}</b>
          </span>
          <span>
            <small>RUT</small>
            <b className="mono">{c.clienteRut ?? '—'}</b>
          </span>
          <span>
            <small>Emisión</small>
            <b>{c.fechaEmision ? fecha(c.fechaEmision) : '—'}</b>
          </span>
          <span>
            <small>Total</small>
            <b className="mono">{pesos(c.total)}</b>
          </span>
          <button type="button" onClick={alCambiarDocumento} disabled={!!resultado}>
            Cambiar documento
          </button>
        </div>
        {c.rechazo && (
          <p className="ncd-aviso-rechazo" role="note">
            <b>Rechazo en la entrega · ODD N° {c.rechazo.odNumero}</b> del {fecha(c.rechazo.fecha)}:
            el cliente no lo quiso, volvió en el camión y bodega ya lo recibió. Va prellenada:
            revisa y emite. Al emitirla, si a la nota no le queda otra cosa por despachar, se cierra
            y se libera lo que quedaba reservado.
          </p>
        )}
      </fieldset>

      <div className="ncd-dos">
        <fieldset className="recuadro-nc" disabled={!!resultado}>
          <legend>Motivo</legend>
          {MOTIVOS.map((m) => (
            <label key={m.id} className="ncd-opcion">
              <input
                type="radio"
                name="ncd-motivo"
                checked={motivo === m.id}
                onChange={() => {
                  setMotivo(m.id);
                  setValores({});
                }}
              />
              <span>
                <b>{m.titulo}</b>
                <small>{m.detalle}</small>
              </span>
            </label>
          ))}
          {motivo === 'DEVOLUCION' && (
            <label className="campo ncd-sub">
              <span className="etiqueta">¿Por qué vuelve?</span>
              <select
                aria-label="Causa de la devolución"
                value={causaDev}
                onChange={(e) => setCausaDev(e.target.value)}
              >
                {CAUSAS_DEVOLUCION.map(([v, t]) => (
                  <option key={v} value={v}>
                    {t}
                  </option>
                ))}
              </select>
            </label>
          )}
          {motivo === 'MONTO' && (
            <label className="campo ncd-sub">
              <span className="etiqueta">Tipo de corrección</span>
              <select
                aria-label="Tipo de corrección"
                value={causaMonto}
                onChange={(e) => setCausaMonto(e.target.value)}
              >
                {CAUSAS_MONTO.map(([v, t]) => (
                  <option key={v} value={v}>
                    {t}
                  </option>
                ))}
              </select>
            </label>
          )}
        </fieldset>

        <fieldset className="recuadro-nc" disabled={!!resultado || !preguntaStock}>
          <legend>Movimiento de stock</legend>
          {!preguntaStock ? (
            <p className="apagado ncd-nota">
              {motivo === 'MONTO' || motivo === 'TEXTO'
                ? 'Esta corrección no mueve stock.'
                : soloLoQueVolvio
                  ? `Volvió en el camión de la ODD N° ${c.rechazo!.odNumero} y bodega ya lo recibió: esta NC no mueve stock.`
                  : 'La mercadería no ha salido: no hay nada que devolver a bodega.'}
            </p>
          ) : (
            <>
              <label className="ncd-opcion">
                <input
                  type="radio"
                  name="ncd-stock"
                  checked={mueve}
                  onChange={() => setMueve(true)}
                />
                <span>
                  <b>Sí, la mercadería vuelve</b>
                  <small>Entra a la bodega que elijas (por omisión, la de salida).</small>
                </span>
              </label>
              <label className="ncd-opcion">
                <input
                  type="radio"
                  name="ncd-stock"
                  checked={!mueve}
                  onChange={() => setMueve(false)}
                />
                <span>
                  <b>No mueve stock</b>
                  <small>
                    {motivo === 'ANULACION'
                      ? 'Se anula y la mercadería queda en el cliente.'
                      : 'Solo se acredita el monto; la mercadería no vuelve.'}
                  </small>
                </span>
              </label>
              {mueve && (
                <label className="campo ncd-sub">
                  <span className="etiqueta">Bodega que recibe</span>
                  <select
                    aria-label="Bodega que recibe"
                    value={ubicacionId}
                    onChange={(e) => {
                      setUbicacionTocada(true);
                      setUbicacionId(e.target.value);
                    }}
                  >
                    {reciben.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.codigo} · {u.nombre}
                        {u.id === c.ubicacionOrigenId ? ' (salida)' : ''}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </>
          )}
        </fieldset>
      </div>

      <fieldset className="recuadro-nc" disabled={!!resultado}>
        <legend>Detalle</legend>
        <table className="tabla-fija ncd-lineas">
          <thead>
            <tr>
              <th>N°</th>
              <th>Código</th>
              <th>Descripción</th>
              <th>Unid.</th>
              <th className="der">Cant.</th>
              <th className="der">En cliente</th>
              {c.rechazo && <th className="der">Volvió en camión</th>}
              <th className="der">Total</th>
              <th className="der">Disponible</th>
              {(pideCantidad || pideMonto) && (
                <th className="der">{pideCantidad ? 'Devuelve' : 'Monto NC'}</th>
              )}
            </tr>
          </thead>
          <tbody>
            {c.lineas.map((l) => (
              <tr key={l.numero}>
                <td>{l.numero}</td>
                <td className="mono">{l.sku}</td>
                <td>{l.descripcion}</td>
                <td>{l.unidad}</td>
                <td className="der mono">{cant(l.cantidad)}</td>
                <td className="der mono">{cant(l.enCliente)}</td>
                {c.rechazo && (
                  <td className="der mono">
                    {volvio.has(l.numero) ? cant(String(volvio.get(l.numero))) : ''}
                  </td>
                )}
                <td className="der mono">{pesos(l.bruto)}</td>
                <td className="der mono">{pesos(l.disponible)}</td>
                {(pideCantidad || pideMonto) && (
                  <td className="der">
                    <input
                      className="ncd-valor"
                      aria-label={`${pideCantidad ? 'Devuelve' : 'Monto'} línea ${l.numero}`}
                      inputMode="decimal"
                      value={valores[l.numero] ?? ''}
                      onChange={(e) =>
                        setValores((v) => ({
                          ...v,
                          [l.numero]: e.target.value.replace(/[^\d.,]/g, '').replace(',', '.'),
                        }))
                      }
                    />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {pideCantidad && (
          <button
            type="button"
            className="ncd-todo"
            onClick={() =>
              setValores(
                Object.fromEntries(
                  c.lineas.map((l) => [
                    l.numero,
                    cant(String(Math.max(num(l.enCliente), 0) || num(l.cantidad))),
                  ]),
                ),
              )
            }
          >
            Devolver todo
          </button>
        )}
        {motivo === 'TEXTO' && (
          <label className="campo">
            <span className="etiqueta">Corrección («donde dice… debe decir…», máx. 80)</span>
            <input
              aria-label="Texto que corrige"
              maxLength={80}
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
            />
          </label>
        )}
        <label className="campo">
          <span className="etiqueta">Descripción (qué pasó; queda en la auditoría)</span>
          <input
            aria-label="Descripción"
            maxLength={400}
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
          />
        </label>
      </fieldset>

      <div className="ncd-pie">
        <span className="ncd-total">
          Monto nota de crédito{motivo === 'DEVOLUCION' ? ' (estimado)' : ''}:{' '}
          <b className="mono">{pesos(String(estimado))}</b>
        </span>
        {!resultado && (
          <button
            type="button"
            className="primario"
            disabled={!!problemas.length || emitir.isPending}
            onClick={enviar}
          >
            {emitir.isPending ? 'Emitiendo…' : 'Emitir nota de crédito (F10)'}
          </button>
        )}
      </div>
      {!resultado && problemas.length > 0 && (
        <ul className="ncd-problemas">
          {problemas.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      )}
      {!!emitir.error && <PanelError error={emitir.error} />}
      {resultado && (
        <div className="resultado tono-ok" role="status" data-nc-directa="emitida">
          <strong>Nota de crédito solicitada: se emite en segundos.</strong>
          {resultado.devoluciones.length > 0 && (
            <span>
              {' '}
              La mercadería entró a bodega (devolución{' '}
              {resultado.devoluciones.map((d) => `N° ${d.numero}`).join(', ')}).
            </span>
          )}
          {resultado.rechazo && resultado.rechazo.odd.length > 0 && (
            <span>
              {' '}
              Rechazo de la ODD N° {resultado.rechazo.odd.join(', ')} resuelto
              {resultado.rechazo.notasCerradas.length
                ? `: la ${resultado.rechazo.notasCerradas.join(', ')} quedó cerrada y se liberó lo que estaba reservado.`
                : '.'}
            </span>
          )}
          {resultado.rechazo && resultado.rechazo.errores.length > 0 && (
            <span className="texto-aviso"> {resultado.rechazo.errores.join(' · ')}</span>
          )}
          <div className="acciones">
            {resultado.notas
              .filter((n) => n.dteDocumentoId)
              .map((n) => (
                <button
                  key={n.ajusteId}
                  type="button"
                  onClick={() => void imprimir(n.dteDocumentoId!)}
                >
                  Imprimir NC {pesos(n.bruto)}
                </button>
              ))}
            <button type="button" className="primario" onClick={alCambiarDocumento}>
              Otra nota de crédito
            </button>
          </div>
          {!!errorPdf && <PanelError error={errorPdf} />}
        </div>
      )}
    </div>
  );
}
